import { PlusIcon } from "vega-icons";

import { type DealDataRoom } from "@vega-api/generated/types/dealDataRoom";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { DataRoomProviderCard } from "../DataRoomProviderCard/DataRoomProviderCard";

type DataRoomProvidersSectionProps = {
  isPending: boolean;
  dataRooms: DealDataRoom[];
  onAddDataRoom: () => void;
  onEditDataRoom: (dataRoom: DealDataRoom) => void;
  onRemoveDataRoom: (dataRoom: DealDataRoom) => void;
};

export const DataRoomProvidersSection = ({
  isPending,
  dataRooms,
  onAddDataRoom,
  onEditDataRoom,
  onRemoveDataRoom,
}: DataRoomProvidersSectionProps) => {
  // Closed deals lock data-room link management (links stay viewable)
  const { isMutationLocked } = useCoInvestDealReadOnly();

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="heading-5 text-text/primary">
          {m.coInvestments_dataRoom_manageDataRooms()}
        </h2>
        {isMutationLocked ? null : (
          <ThemedButton
            dataTestId="add-data-room-button"
            icon={<PlusIcon className="size-4" />}
            onClick={onAddDataRoom}
            size="small"
            variant="secondary"
          >
            {m.coInvestments_dataRoom_addAction()}
          </ThemedButton>
        )}
      </div>

      {isPending ? (
        <div className="space-y-4">
          {Array(3)
            .fill(null)
            .map((_, index) => (
              <div
                className="flex items-center justify-between p-4 bg-primary border border-border rounded-lg"
                // eslint-disable-next-line react/no-array-index-key
                key={`skeleton-card-${index}`}
              >
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
        </div>
      ) : (
        <div className="space-y-4">
          {dataRooms.map((dataRoom) => (
            <DataRoomProviderCard
              dataRoom={dataRoom}
              key={dataRoom.dataRoomProvider.name}
              onEdit={onEditDataRoom}
              onRemove={onRemoveDataRoom}
              readOnly={isMutationLocked}
            />
          ))}
        </div>
      )}
    </div>
  );
};
